import { useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ImagePlus, Video, X } from "lucide-react";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { PennField, PennInput, PennRichText } from "@/components/ui";
import type { LandingContentFormData, LandingImage, LandingVideo } from "@/types/admin/landingContent.types";
import { landingContentSchema } from "@/types/admin/landingContent.types";
import {
  useLandingContentTree,
  useSaveLandingCategory,
  useDeleteLandingCategory,
} from "@/hooks/useLandingContent";
import {
  uploadLandingImage,
  uploadLandingVideo,
  LANDING_VIDEO_MAX_BYTES,
  type LandingCategory,
} from "@/api/landingContent";

type LCItem = LandingContentFormData & { _id: number };
type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: LCItem } | null;

const blank: LandingContentFormData = { key: "", title: "", subtitle: "", paragraph: "", bullets: "", images: [], video: null };

/** Strip HTML tags for a compact table preview. */
const stripHtml = (html?: string) => (html ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ── Bridge the normalised backend (ordered plain-text fragments + image rows)
//    to the flat single-rich-text UI model, and back. ──
const paragraphsToHtml = (ps: { paragraphText: string | null }[]) =>
  ps.map((p) => `<p>${escapeHtml(p.paragraphText ?? "")}</p>`).join("");

const bulletsToHtml = (bs: { bulletText: string | null }[]) =>
  bs.length ? `<ul>${bs.map((b) => `<li>${escapeHtml(b.bulletText ?? "")}</li>`).join("")}</ul>` : "";

const htmlToParagraphs = (html?: string): { paragraphText: string }[] => {
  const doc = new DOMParser().parseFromString(html ?? "", "text/html");
  const blocks = Array.from(doc.body.querySelectorAll("p, div"));
  const texts = blocks.length
    ? blocks.map((b) => (b.textContent ?? "").trim())
    : [(doc.body.textContent ?? "").trim()];
  return texts.filter(Boolean).map((t) => ({ paragraphText: t }));
};

const htmlToBullets = (html?: string): { bulletText: string }[] => {
  const doc = new DOMParser().parseFromString(html ?? "", "text/html");
  const lis = Array.from(doc.body.querySelectorAll("li"));
  const texts = lis.length
    ? lis.map((li) => (li.textContent ?? "").trim())
    : (doc.body.textContent ?? "").split("\n").map((s) => s.trim());
  return texts.filter(Boolean).map((t) => ({ bulletText: t }));
};

/** Map an admin-tree category into the flat UI row. */
const categoryToItem = (cat: LandingCategory, index: number): LCItem => ({
  _id: index + 1,
  key: cat.categoryKey,
  title: cat.title ?? "",
  subtitle: cat.subtitle ?? "",
  paragraph: paragraphsToHtml(cat.paragraphs),
  bullets: bulletsToHtml(cat.bullets),
  images: cat.images.map((im) => ({
    url: im.imageUrl ?? "",
    subtitle: im.caption ?? "",
    displayUrl: im.imageDisplayUrl ?? im.imageUrl ?? undefined,
  })),
  video: cat.videoUrl
    ? { url: cat.videoUrl, displayUrl: cat.videoDisplayUrl ?? cat.videoUrl }
    : null,
});

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/* ── Image uploader: max 2 images, each uploaded to S3 (mirrors 3D CAD) ── */
const ImageUploader = ({
  value,
  onChange,
  disabled,
}: {
  value: LandingImage[];
  onChange: (v: LandingImage[]) => void;
  disabled?: boolean;
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    const room = 2 - value.length;
    if (room <= 0) return;
    const picked = Array.from(files).slice(0, room);
    if (picked.length === 0) return;

    setUploading(true);
    setUploadError(null);
    try {
      // Upload each picked file to S3; store the durable key as `url` and the
      // presigned `displayUrl` for preview. Bytes never go into the form/DB.
      const uploaded = await Promise.all(
        picked.map(async (f): Promise<LandingImage> => {
          const res = await uploadLandingImage(f);
          return { url: res.key, subtitle: "", displayUrl: res.displayUrl };
        }),
      );
      onChange([...value, ...uploaded]);
    } catch (err) {
      setUploadError(apiErrorMessage(err, "Failed to upload image."));
    } finally {
      setUploading(false);
    }
  };

  const setSubtitle = (i: number, subtitle: string) =>
    onChange(value.map((img, idx) => (idx === i ? { ...img, subtitle } : img)));

  const remove = (i: number) =>
    onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {value.map((img, i) => (
          <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
            <div className="relative aspect-video rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex items-center justify-center">
              {img.displayUrl ? (
                <img src={img.displayUrl} alt={img.subtitle || img.url} className="object-contain w-full h-full" />
              ) : (
                <span className="px-2 text-center text-blue_grey4 truncate text-small">{img.url}</span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                  aria-label="Remove image"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <PennInput
              className="mt-2"
              placeholder="Image subtitle"
              value={img.subtitle ?? ""}
              disabled={disabled}
              onChange={(e) => setSubtitle(i, e.target.value)}
            />
          </div>
        ))}

        {!disabled && value.length < 2 && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex aspect-video flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-blue_grey4 hover:border-penn-400 hover:text-penn-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ImagePlus size={20} />
            <span className="text-small-medium">{uploading ? "Uploading…" : "Upload image"}</span>
            <span className="text-caption text-blue_grey4">{value.length}/2</span>
          </button>
        )}
      </div>

      {uploadError && (
        <p className="text-red-600 dark:text-red-400 text-small">{uploadError}</p>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          void addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
};

/* ── Video uploader: single hero video, ≤300 MB, uploaded directly to S3 via a
   presigned PUT (bytes never traverse the gateway). Mirrors ImageUploader. ── */
const VideoUploader = ({
  value,
  onChange,
  disabled,
}: {
  value: LandingVideo | null;
  onChange: (v: LandingVideo | null) => void;
  disabled?: boolean;
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const addFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setUploadError("Please choose a video file.");
      return;
    }
    if (file.size > LANDING_VIDEO_MAX_BYTES) {
      setUploadError("Video exceeds the 300 MB limit.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      // Presign + PUT straight to S3; store the durable key as `url` and the
      // presigned `displayUrl` for preview. Bytes never go into the form/DB.
      const res = await uploadLandingVideo(file);
      onChange({ url: res.key, displayUrl: res.displayUrl });
    } catch (err) {
      setUploadError(apiErrorMessage(err, "Failed to upload video."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {value ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2 max-w-md">
          <div className="relative aspect-video rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex items-center justify-center">
            {value.displayUrl ? (
              <video src={value.displayUrl} controls preload="metadata" className="w-full h-full object-contain" />
            ) : (
              <span className="px-2 text-center text-blue_grey4 truncate text-small">{value.url}</span>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                aria-label="Remove video"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex aspect-video w-full max-w-xs flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 text-blue_grey4 hover:border-penn-400 hover:text-penn-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Video size={20} />
            <span className="text-small-medium">{uploading ? "Uploading…" : "Upload video"}</span>
            <span className="text-caption text-blue_grey4">max 300 MB</span>
          </button>
        )
      )}

      {uploadError && (
        <p className="text-red-600 dark:text-red-400 text-small">{uploadError}</p>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => {
          void addFile(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export const LandingContentPage = () => {
  const { data: tree, isLoading, isError, error } = useLandingContentTree();
  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<LCItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Keep the raw backend categories around so we can preserve fields the flat
  // UI model doesn't surface (image productId, category tip, sortOrder).
  const originalByKey = useMemo(() => {
    const map = new Map<string, LandingCategory>();
    (tree?.categories ?? []).forEach((c) => map.set(c.categoryKey, c));
    return map;
  }, [tree]);

  const items: LCItem[] = useMemo(
    () => (tree?.categories ?? []).map(categoryToItem),
    [tree],
  );

  const saveMutation = useSaveLandingCategory();
  const deleteMutation = useDeleteLandingCategory();

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<LandingContentFormData>({ resolver: zodResolver(landingContentSchema) });

  const openAdd = () => { setFormError(null); reset(blank); setModal({ mode: "add" }); };
  const openEdit = (item: LCItem) => { setFormError(null); reset(item); setModal({ mode: "edit", item }); };
  const openView = (item: LCItem) => { setFormError(null); reset(item); setModal({ mode: "view", item }); };
  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (data: LandingContentFormData) => {
    setFormError(null);
    const mode = modal?.mode === "edit" ? "edit" : "add";
    const key = mode === "edit" ? (modal!.item!.key) : data.key.trim();
    const original = originalByKey.get(key);

    // Preserve productId for unchanged images (match by URL); new uploads → null.
    const productIdByUrl = new Map<string, string | null>();
    (original?.images ?? []).forEach((im) => {
      if (im.imageUrl) productIdByUrl.set(im.imageUrl, im.productId ?? null);
    });

    const children = {
      paragraphs: htmlToParagraphs(data.paragraph),
      bullets: htmlToBullets(data.bullets),
      images: (data.images ?? [])
        .filter((im) => !!im.url)
        .map((im) => ({
          productId: productIdByUrl.get(im.url) ?? null,
          imageUrl: im.url,
          caption: im.subtitle?.trim() || null,
        })),
    };

    const sortOrder =
      mode === "edit"
        ? original?.sortOrder ?? null
        : (items.reduce((max, _it, idx) => Math.max(max, originalByKey.get(items[idx].key)?.sortOrder ?? 0), 0) + 1);

    const videoUrl = data.video?.url ?? null;

    const category =
      mode === "add"
        ? { categoryKey: key, title: data.title.trim(), subtitle: data.subtitle?.trim() || null, tip: null, videoUrl, sortOrder }
        : { title: data.title.trim(), subtitle: data.subtitle?.trim() || null, tip: original?.tip ?? null, videoUrl, sortOrder };

    saveMutation.mutate(
      { mode, key, category, children },
      {
        onSuccess: closeModal,
        onError: (e) => setFormError(apiErrorMessage(e, "Failed to save landing content.")),
      },
    );
  };

  const isView = modal?.mode === "view";
  const saving = saveMutation.isPending;

  const cols: ColDef<LCItem>[] = [
    { header: "_key", key: "key" },
    { header: "Title", key: "title" },
    { header: "Subtitle", key: "subtitle" },
    { header: "Paragraph", render: (i) => <span className="text-blue_grey3 line-clamp-1 text-small">{stripHtml(i.paragraph)}</span> },
    { header: "Bullets", render: (i) => <span className="text-blue_grey3 line-clamp-1 text-small">{stripHtml(i.bullets)}</span> },
    { header: "Images", render: (i) => <span className="text-blue_grey4 text-small">{i.images.length ? `${i.images.length} image(s)` : "—"}</span> },
    { header: "Video", render: (i) => <span className="text-blue_grey4 text-small">{i.video ? "1 video" : "—"}</span> },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: "Add record", edit: "Edit Landing Content record", view: "View Landing Content record" };

  return (
    <>
      <PageHeader title="Dataset: Landing Content" subtitle="Record-level editor for categories (_id: _key)." action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load landing content{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <AdminTable columns={cols} rows={items} keyExtractor={(i) => i._id} loading={isLoading} />

      <AdminModal open={modal !== null} onClose={closeModal} title={modal ? titleMap[modal.mode] : ""} maxWidth="max-w-2xl"
        footer={
          isView ? <CancelButton onClick={closeModal} /> :
          <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PennField label="_key" htmlFor="key" required={!isView} error={!isView ? errors.key?.message : undefined}>
              <PennInput id="key" disabled={isView || modal?.mode === "edit"} error={!isView && !!errors.key} {...register("key")} />
            </PennField>
            <PennField label="Title" htmlFor="title" required={!isView} error={!isView ? errors.title?.message : undefined}>
              <PennInput id="title" disabled={isView} error={!isView && !!errors.title} {...register("title")} />
            </PennField>
          </div>
          <PennField label="Subtitle" htmlFor="subtitle" error={!isView ? errors.subtitle?.message : undefined}>
            <PennInput id="subtitle" disabled={isView} {...register("subtitle")} />
          </PennField>

          <PennField label="Paragraph" htmlFor="paragraph" error={!isView ? errors.paragraph?.message : undefined}>
            <Controller
              name="paragraph"
              control={control}
              render={({ field }) => (
                <PennRichText
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  disabled={isView}
                  placeholder="Write the category paragraph…"
                />
              )}
            />
          </PennField>

          <PennField label="Bullets" htmlFor="bullets" error={!isView ? errors.bullets?.message : undefined}>
            <Controller
              name="bullets"
              control={control}
              render={({ field }) => (
                <PennRichText
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  disabled={isView}
                  placeholder="Add bullet points…"
                />
              )}
            />
          </PennField>

          <PennField label="Images (max 2)" htmlFor="images" error={!isView ? errors.images?.message : undefined}>
            <Controller
              name="images"
              control={control}
              render={({ field }) => (
                <ImageUploader value={field.value ?? []} onChange={field.onChange} disabled={isView} />
              )}
            />
          </PennField>

          <PennField label="Video (max 300 MB)" htmlFor="video" error={!isView ? errors.video?.message : undefined}>
            <Controller
              name="video"
              control={control}
              render={({ field }) => (
                <VideoUploader value={field.value ?? null} onChange={field.onChange} disabled={isView} />
              )}
            />
          </PennField>
        </form>
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget.key, { onSettled: () => setDeleteTarget(null) });
        }}
        itemLabel={deleteTarget?.key}
      />
    </>
  );
};
